import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Loader2, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AppShell from "@/components/layout/AppShell";
import BuilderStatus from "@/components/sites/BuilderStatus";
import QueuePanel from "@/components/sites/QueuePanel";
import SitesTable from "@/components/sites/SitesTable";
import NewSiteDialog from "@/components/sites/NewSiteDialog";
import { SITE_STATUS } from "@/components/sites/helpers";
import { errMsg, useApi } from "@/lib/api";

const OUTCOME = { created: "in coda", updated: "già in coda (aggiornata)", skipped: "già in coda", not_found: "non trovato", project_deleted: "progetto eliminato", images_blocked: "bloccato: immagini non consentite" };

function BulkBar({ selected, onDone }) {
  const api = useApi();
  const [preview, setPreview] = useState(true);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sites/bulk-build", { site_ids: selected, set_preview: preview });
      const other = data.results.filter((r) => r.outcome !== "created");
      toast.success(data.queued === 1 ? "1 compilazione messa in coda" : `${data.queued} compilazioni messe in coda`, { description: other.map((r) => `${r.name || r.site_id}: ${OUTCOME[r.outcome]}`).join(" · ") || undefined });
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-sky-100 bg-sky-50 px-4 py-2.5 text-sm" data-testid="sites-bulk-bar">
      <span className="font-medium text-sky-900">{selected.length} selezionati</span>
      <label className="inline-flex items-center gap-2 text-xs text-slate-700"><Switch checked={preview} onCheckedChange={setPreview} data-testid="bulk-set-preview-switch" />Aggiorna anche l'anteprima</label>
      <Button size="sm" onClick={run} disabled={busy} className="ml-auto bg-sky-600 hover:bg-sky-700" data-testid="bulk-build-btn">
        {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Layers size={13} className="mr-1.5" />}Ricompila selezionati
      </Button>
    </div>
  );
}

export default function Sites() {
  const api = useApi();
  const [sp, setSp] = useSearchParams();
  const [sites, setSites] = useState(null);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);
  const [creating, setCreating] = useState(false);
  const client = sp.get("client") || "", status = sp.get("status") || "";
  const setFilter = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const load = useCallback(() => api.get("/sites", { params: { client_id: client, status } }).then(({ data }) => setSites(data)).catch((e) => { toast.error(errMsg(e)); setSites([]); }), [api, client, status]);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)).catch(() => {}); }, [api]);
  const shown = useMemo(() => (sites || []).filter((s) => !q || `${s.name} ${s.slug} ${s.client.name}`.toLowerCase().includes(q.toLowerCase())), [sites, q]);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  const build = async (s) => {
    try { const { data } = await api.post(`/sites/${s.id}/deploys`, { set_preview: true }); toast.success(`«${s.name}»: compilazione n. ${data.deploy.number} ${data.outcome === "created" ? "in coda" : "già in coda, aggiornata"}`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AppShell>
      <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-7xl space-y-6" data-testid="sites-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-sky-700 font-medium"><Link to="/" className="hover:underline" data-testid="sites-back-link">Siti web</Link> · Operazioni avanzate</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Compilazioni e pubblicazione</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">Ogni sito è compilato da un progetto in pagine statiche veloci. Dove vengono pubblicati (Studio, emulatore di prova o Cloudflare) si sceglie in «Hosting dei siti»; il collegamento dei domini dei clienti non è ancora disponibile.</p>
          </div>
          <Button onClick={() => setCreating(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="new-site-btn"><Plus size={15} className="mr-1.5" />Nuovo sito</Button>
        </div>
        <BuilderStatus />
        <QueuePanel refreshKey={tick} onSettled={refresh} />
        <section className="rounded-xl border border-slate-200 bg-white" data-testid="sites-list">
          <div className="flex flex-wrap items-center gap-3 p-4">
            <div className="relative min-w-[200px] flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca sito o cliente" className="pl-8" data-testid="sites-search-input" /></div>
            <Select value={client || "all"} onValueChange={(v) => setFilter("client", v === "all" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="sites-client-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti i clienti</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={status || "all"} onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}>
              <SelectTrigger className="w-44" data-testid="sites-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{Object.entries(SITE_STATUS).filter(([k]) => k !== "archiviato").map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {selected.length > 0 && <BulkBar selected={selected} onDone={() => { setSelected([]); refresh(); }} />}
          {sites === null ? <p className="flex items-center gap-2 p-5 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" />Caricamento…</p>
            : <SitesTable sites={shown} selected={selected} setSelected={setSelected} onBuild={build} />}
        </section>
      </div>
      <NewSiteDialog open={creating} onOpenChange={setCreating} defaultClient={client} />
    </AppShell>
  );
}
